import { REGISTRY, type Registry } from '../contract/registry';

/**
 * Reduces a Statsig `initialize` response to the registered keys before it leaves the frontend for
 * `chrome.storage.local`. The response covers the whole product, and everything the chrome layer
 * does not read is just data at rest on the user's disk.
 *
 * Keeps `feature_gates` and `dynamic_configs` (there is no `layer` kind), re-keys each entry from its
 * hashed name to its plain one so the chrome layer needs no hasher, and allowlists entry fields with
 * `secondary_exposures` emptied, since it names other gates and only feeds exposure logging.
 *
 * The trade-off: the payload is now coupled to the *producer's* registry, and the frontend deploys
 * ahead of an installed extension build. A key the frontend has already retired resolves to its
 * registry default here sooner than it used to, so retire in the documented order, call sites first.
 */
export interface SanitisedValues {
	readonly values: Record<string, unknown>;
	/** The registered keys an entry was actually found for. Everything else fell back to defaults. */
	readonly kept: readonly string[];
	/** Set when `hash_used` named an algorithm this cannot reproduce, in which case nothing is kept. */
	readonly unsupportedHash?: string;
	/**
	 * Set when the input was not a values blob at all, as distinct from "nothing matched": the caller
	 * must refuse to publish rather than overwrite a good envelope with an empty one.
	 */
	readonly invalidInput?: true;
}

/** Fields the SDK evaluates from. Anything else in an entry is dropped. */
const ENTRY_FIELDS = [
	'name',
	'value',
	'rule_id',
	'group',
	'group_name',
	'id_type',
	'is_device_based',
	'is_experiment_active',
	'is_user_in_experiment',
	'passed',
] as const;

export async function sanitiseExperimentValues(
	rawValues: unknown,
	registry: Registry = REGISTRY,
): Promise<SanitisedValues> {
	if (!isRecord(rawValues)) {
		return { values: {}, kept: [], invalidInput: true };
	}

	const base: Record<string, unknown> = {
		feature_gates: {},
		dynamic_configs: {},
		has_updates: true,
		hash_used: 'none',
		// The SDK's own timestamp for the values. `fetchedAt` is this producer's clock, not a substitute.
		...(typeof rawValues.time === 'number' ? { time: rawValues.time } : {}),
	};

	const hashUsed = typeof rawValues.hash_used === 'string' ? rawValues.hash_used : 'none';
	const hashName = hasherFor(hashUsed);
	if (!hashName) {
		return { values: base, kept: [], unsupportedHash: hashUsed };
	}

	const gates: Record<string, unknown> = {};
	const configs: Record<string, unknown> = {};
	const kept: string[] = [];

	for (const [key, definition] of Object.entries(registry)) {
		const section = definition.kind === 'gate' ? 'feature_gates' : 'dynamic_configs';
		const served = asRecord(rawValues[section])?.[await hashName(key)];
		if (!isRecord(served)) {
			continue;
		}
		const target = definition.kind === 'gate' ? gates : configs;
		target[key] = trimEntry(served, key);
		kept.push(key);
	}

	return { values: { ...base, feature_gates: gates, dynamic_configs: configs }, kept };
}

/** Keeps the allowlisted fields, renames the entry to its plain name, and empties the exposures. */
function trimEntry(entry: Record<string, unknown>, plainName: string): Record<string, unknown> {
	const out: Record<string, unknown> = {};
	for (const field of ENTRY_FIELDS) {
		if (field in entry) {
			out[field] = entry[field];
		}
	}
	out.name = plainName;
	out.secondary_exposures = [];
	return out;
}

/**
 * The name hashers Statsig uses for the `initialize` response. An unrecognised one is not guessed at:
 * the caller publishes nothing, so keys take their registry default over a value matched by luck.
 */
function hasherFor(hashUsed: string): ((name: string) => Promise<string>) | undefined {
	switch (hashUsed) {
		case 'none':
			return (name) => Promise.resolve(name);
		case 'djb2':
			return (name) => Promise.resolve(djb2(name));
		case 'sha256':
			return sha256Base64;
		default:
			return undefined;
	}
}

function djb2(name: string): string {
	let hash = 0;
	for (let index = 0; index < name.length; index++) {
		hash = (hash << 5) - hash + name.charCodeAt(index);
		// Statsig's implementation works on a 32-bit integer and formats the unsigned result.
		hash = hash & 0xffffffff;
	}
	return String(hash >>> 0);
}

async function sha256Base64(name: string): Promise<string> {
	if (!globalThis.crypto?.subtle) {
		throw new Error('Feature gates: WebCrypto subtle is unavailable, cannot hash entry names');
	}
	const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(name));
	let binary = '';
	for (const byte of new Uint8Array(digest)) {
		binary += String.fromCharCode(byte);
	}
	return btoa(binary);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
	return isRecord(value) ? value : undefined;
}
